// PATH: src/components/catalog/forms/OfferingDialog.tsx
import * as React from "react";
import { Autocomplete, Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/CheckRounded";
import { useOfferingsApi } from "../../../api";
import type { CurriculumGrade, CurriculumOffering, Subject } from "../../../models/domain";
import { nextSortOrder, offerableSubjects, parseOptionalInt, sameIdSet, sortTracks, subjectColor } from "../catalogModel";
import { ColorDot } from "../PlanetPreview";
import { FormDialog } from "./FormDialog";

const selectedSx = {
  bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
  borderColor: "primary.main",
  "&&:hover": { bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.14) },
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  grade: CurriculumGrade;
  /** Matières du programme. */
  subjects: Subject[];
  /** Offre éditée ; absente = proposer une matière. */
  offering?: CurriculumOffering | null;
}

/**
 * Offre = une matière proposée à un niveau, éventuellement réservée à
 * certaines filières (`track_ids` ; aucune = toutes les filières du niveau).
 */
export const OfferingDialog: React.FC<Props> = ({ open, onClose, grade, subjects, offering }) => {
  const { createOffering, updateOffering, setOfferingTracks } = useOfferingsApi();
  const tracks = React.useMemo(() => sortTracks(grade.tracks), [grade.tracks]);
  const current = offering?.offering;
  const options = React.useMemo(
    () => offerableSubjects(subjects, grade, current?.subject_id),
    [subjects, grade, current?.subject_id],
  );

  const [subjectId, setSubjectId] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState("");
  const [trackIds, setTrackIds] = React.useState<string[]>([]);
  const [touched, setTouched] = React.useState(false);
  /** Offre créée par un essai précédent dont l'écriture des filières a échoué. */
  const createdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    createdRef.current = null;
    setSubjectId(current?.subject_id ?? null);
    setOrder(String(current ? current.sort_order ?? 0 : nextSortOrder((grade.offerings ?? []).map((o) => o.offering))));
    setTrackIds(current?.track_ids ?? []);
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.id]);

  const parsedOrder = parseOptionalInt(order);
  const errors = {
    subject: !subjectId ? "Choisissez une matière." : "",
    order: parsedOrder === null ? "Requis." : Number.isNaN(parsedOrder) ? "Un nombre entier." : "",
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : "");
  const toggle = (id: string) =>
    setTrackIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (!subjectId) return;
    const base = { subject_id: subjectId, grade_id: grade.grade.id, sort_order: parsedOrder ?? 0 };
    let id = current?.id ?? createdRef.current;
    if (!id) {
      id = (await createOffering(base)).data?.id ?? null;
      createdRef.current = id;
    } else if (!current || current.subject_id !== subjectId || current.sort_order !== base.sort_order) {
      await updateOffering(id, base);
    }
    if (id && tracks.length > 0 && !sameIdSet(current?.track_ids, trackIds)) {
      await setOfferingTracks(id, trackIds);
    }
  };

  const selected = options.find((s) => s.id === subjectId) ?? null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={current ? "Modifier l'offre" : "Proposer une matière"}
      context={`Niveau : ${grade.grade.name}`}
      entity="offering"
      submitLabel={current ? "Enregistrer" : "Proposer"}
      successMessage={current ? "Offre enregistrée." : "Matière proposée au niveau."}
      maxWidth="sm"
      validate={() => {
        setTouched(true);
        return !errors.subject && !errors.order;
      }}
      onSubmit={submit}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Autocomplete
            fullWidth
            options={options}
            value={selected}
            onChange={(_, v) => setSubjectId(v?.id ?? null)}
            getOptionLabel={(s) => s.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Toutes les matières du programme sont déjà proposées ici."
            renderOption={({ key, ...props }, s) => (
              <Box component="li" key={key} {...props} sx={{ gap: 1.25 }}>
                <ColorDot color={subjectColor(s)} />
                <Box sx={{ flex: 1, minWidth: 0 }} dir="auto">
                  {s.name}
                </Box>
                <Typography variant="caption" color="text.secondary" className="mono">
                  {s.code}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus={!current}
                label="Matière"
                required
                error={Boolean(show("subject"))}
                helperText={show("subject") || " "}
              />
            )}
          />
          <TextField
            label="Rang"
            required
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            error={Boolean(show("order"))}
            helperText={show("order") || " "}
            sx={{ width: 110, flexShrink: 0 }}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
        </Stack>

        {tracks.length > 0 ? (
          <Box>
            <Typography variant="subtitle2">Filières concernées</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Aucune filière cochée : la matière vaut pour toutes les filières du niveau, y compris celles ajoutées plus
              tard.
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
              <Chip
                label="Toutes les filières"
                icon={trackIds.length === 0 ? <CheckIcon /> : undefined}
                color={trackIds.length === 0 ? "primary" : "default"}
                variant="outlined"
                onClick={() => setTrackIds([])}
                sx={trackIds.length === 0 ? selectedSx : undefined}
              />
              {tracks.map((t) => {
                const on = trackIds.includes(t.id);
                return (
                  <Chip
                    key={t.id}
                    label={
                      <span>
                        <span className="mono">{t.code}</span>
                        <Box component="span" sx={{ color: on ? "inherit" : "text.secondary", ml: 0.75 }}>
                          {t.name}
                        </Box>
                      </span>
                    }
                    icon={on ? <CheckIcon /> : undefined}
                    color={on ? "primary" : "default"}
                    variant="outlined"
                    onClick={() => toggle(t.id)}
                    sx={{ opacity: t.is_active ? 1 : 0.6, ...(on ? selectedSx : {}) }}
                    title={t.is_active ? undefined : "Filière inactive"}
                  />
                );
              })}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Ce niveau n'a pas de filière : la matière vaut pour tous ses élèves.
          </Typography>
        )}
      </Stack>
    </FormDialog>
  );
};

export default OfferingDialog;
